import { Box, Typography, useTheme } from "@mui/material";
import { Ref, forwardRef, useEffect, useImperativeHandle } from "react";
import { useTranslation } from "react-i18next";
import { UIRefreshCategoryEnum } from "../../../common/enums/UIRefreshCategoryEnum";
import { eLynxObjectTypeEnum } from "../../../common/enums/eLynxObjectType";
import { BaseActionEnum } from "../../../components/action-menu/ActionMenuItem";
import { ContentContainer } from "../../../components/content-container/ContentContainer";
import { ContentSection } from "../../../components/content-section/ContentSection";
import { ViewOptionStateEnum } from "../../../components/contextual-app-bar/ViewOptionStateEnum";
import LoadingZone from "../../../components/loading-zone/LoadingZone";
import NoData from "../../../components/no-data/NoData";
import OptionsSideSheet from "../../../components/options-side-sheet/OptionsSideSheet";
import SideSheetFilterInput from "../../../components/side-sheet/side-sheet-filter-input/SideSheetFilterInput";
import SummaryListingGrid from "../../../components/summary-listing-grid/SummaryListingGrid";
import { useProgressContext } from "../../../contexts/progress-context/ProgressContext";
import { useUserContext } from "../../../contexts/user-context/UserContext";
import { useRouteContext } from "../../../hooks/use-route-context/UseRouteContext";
import { DEFAULT_REFRESH_SECONDS } from "../../../queries/BaseUseQueryParameters";
import { useQueryNotifications } from "../../../queries/UseQueryNotifications";
import { useCompressorLynxSummaryData } from "../../../queries/group-summary/compressorlynx/UseCompressorLynxSummaryData";
import { actionItems } from "../SummaryListingActions";
import { useSummaryListingContext } from "../SummaryListingContext";
import { postSortCompressorLynxOnAlarms } from "../common/Common";
import useCompressorLynxViewState from "./CompressorLynxViewState";
import { createGridData } from "./CompressorLynxViewUtils";
import SideSheetCheckbox from "../../side-sheet/side-sheet-checkbox/SideSheetCheckbox";
import _ from "lodash";
import { useEvaluateFeature } from "../../../queries/features/UseEvaluateFeature";
import { FeatureConstants } from "../../../services/features-data-service/Models";
import { useUserSettings } from "../../../queries/user-settings/UseUserSettings";
import { useOfflineDetectContext } from "../../../contexts/offline-detect-context/UseOfflineDetectContext";

export type CompressorLynxViewProps = {
    visible?: boolean;
    setViewOptionState?: (state: ViewOptionStateEnum) => void;
};

export type CompressorLynxViewType = {
    executeAction: (action: BaseActionEnum) => void;
};

function CompressorLynxView({
    visible,
    setViewOptionState
}: CompressorLynxViewProps, ref: Ref<unknown> | undefined) {
    const { t } = useTranslation();
    const routeContext = useRouteContext();
    const noDataMessage = t("summaryListing.nodata", { objectType: 'compressorlynx data' });
    const noDataFilterMessage = t("summaryListing.nodataFilter", { objectType: 'compressorlynx data' });
    const { /*state: summaryListingState,*/ setActions, setCompressorLynxCount } = useSummaryListingContext();
    const [userState] = useUserContext();
    const [progressState] = useProgressContext();
    const [offlineState] = useOfflineDetectContext();    
    

    const {
        state,
        resetOptions,
        setFilterText,
        setNoData,
        setRefreshing,
        setAlarmsAtTop
    } = useCompressorLynxViewState();

    useImperativeHandle<unknown, CompressorLynxViewType>(
        ref,
        () => ({
            executeAction: executeAction
        })
    );

    const { data: summaryData, status, refetch } = useQueryNotifications(useCompressorLynxSummaryData)({
        groupID: routeContext.groupID as number,
        enabled: true,
        refreshSeconds: userState.refreshIntervals.find(o => o.category === UIRefreshCategoryEnum.SummaryListing)?.refreshSeconds ?? DEFAULT_REFRESH_SECONDS,
        onSuccess: () => {
            setTimeout(() => setRefreshing(false), 2000);
        },
        onError: () => {
            setRefreshing(false);
        }

    });

    const { data: headerAndActionsV2Feature } = useQueryNotifications(useEvaluateFeature)({
        feature: FeatureConstants.HeaderAndActionsV2,
    });

    const headerAndActionsV2 = (headerAndActionsV2Feature ?? false);

    const { data: userSettings } = useQueryNotifications(useUserSettings)({
        enabled: !offlineState.offline
    });



    const theme = useTheme();

    const data = createGridData(summaryData, routeContext.groupID as number, theme);

    useEffect(() => {
        const rowCount = summaryData?.rows.length ?? 0;
        setCompressorLynxCount(rowCount);
        setNoData(rowCount === 0);
    }, [summaryData, setCompressorLynxCount, setNoData]);

    useEffect(() => {
        if (visible) {
            const viewOptionState = state.isOptionsDirty ? ViewOptionStateEnum.Changed : ViewOptionStateEnum.Default;
            setViewOptionState?.(viewOptionState);
        }
    }, [
        visible,
        state.isOptionsDirty,
        setViewOptionState
    ]);

    // Don't want to do this. This hides the grid when column filters are applied
    // that result in to data. If the grid is hidden, the user can't see the filters
    // anymore.
    // function onFilterChanged(event: FilterChangedEvent) {
    //     setNoData(event.api.getRenderedNodes().length === 0);
    // }

    function executeAction(action: BaseActionEnum) {
        switch (action) {
            case BaseActionEnum.Refresh:
                setRefreshing(true);
                refetch();
                break;
        }
    }

    function onResetOptions() {

        resetOptions();
    }

 

    function onDisplayAlarmsFirst(checked: boolean) {
        setAlarmsAtTop(checked);
    }    

    // get progress running for current object
    const progress = progressState.items.find(o => o.objectID === routeContext.groupID && o.objectType === eLynxObjectTypeEnum.Group);

    useEffect(() => {

        if (visible) {

            const items = [...actionItems].filter(o => o.action === BaseActionEnum.Refresh || o.action === BaseActionEnum.Export);

            const exportAction = items.find(o => o.action === BaseActionEnum.Export);

            if (exportAction) {
                exportAction.disabled = (progress && progress.uid === state.downloadUid) || (summaryData?.rows?.length ?? 0) === 0;
            }

            // if (!_.isEqual(items, summaryListingState.actions)) {
                setActions(items);
            // }
        }
    }, [
        visible,
        progress,
        setActions,
        state.downloadUid,
        summaryData,
        // summaryListingState.actions
    ]);
    
    useEffect(() => {
        if (userSettings) {
            setAlarmsAtTop(userSettings.summaryListingAlarmAtTop);
        }

    },[
        userSettings,
        setAlarmsAtTop,
    ]);


    return (
        <>
            {visible &&
                <Box sx={{
                    display: "flex",
                    flexDirection: "column",
                    flexGrow: 1,
                    overflow: "hidden",
                }}>
                    <LoadingZone isLoading={status === "loading" || state.refreshing}>
                        <ContentContainer>
                            <ContentSection>

                                <SummaryListingGrid
                                    show={state.isNoData === false}
                                    gridData={data}
                                    showFooter={true}
                                    filterText={state.filterText}
                                    postSort={state.summaryListingAlarmAtTop
                                        ? postSortCompressorLynxOnAlarms
                                        : undefined
                                    }
                                    // onFilterChanged={onFilterChanged}
                                    getRowId={data => data.data.tagID.toString()}
                                />

                                {state.isNoData && (status === "success" || status === "error") &&
                                    <NoData text={data.rows.length === 0 ? noDataMessage : noDataFilterMessage} />
                                }

                            </ContentSection>

                            <OptionsSideSheet onReset={onResetOptions}>
                                <SideSheetFilterInput
                                    label="Find in CompressorLynx List"
                                    value={state.filterText}
                                    onChange={(value) => {
                                        setFilterText(value);
                                    }}
                                />
                                {headerAndActionsV2 ? 
                                <>
                                <Typography sx={{
                                    fontWeight: 600
                                }}>
                                    Display
                                </Typography>


                                <SideSheetCheckbox
                                    //name="alarmsAtTop"
                                    sx={{color: theme.palette.text.secondary}}
                                    label={t('profile.alarmFirst', {objectType: "Devices"})}
                                    checked={state.summaryListingAlarmAtTop}
                                    onChange={(checked) => onDisplayAlarmsFirst(checked)}
                                />
                                </> : 
                                null
                                }

                            </OptionsSideSheet>
                        </ContentContainer>
                    </LoadingZone>
                </Box>
            }
        </>
    );
}

export default forwardRef(CompressorLynxView);